import React from 'react';
import ReactDOM from 'react-dom';
import { ColorPickerComponent, ColorPickerEventArgs } from "@syncfusion/ej2-react-inputs";
import colorPickerPalette from './colorPickerPalette';

const { useEffect, useRef, useState } = React;

interface ColorPickerWithNoColorCheckboxProps {
	value?: number | null;
	onChange: (value: number | null) => void;
	disabled?: boolean;
	id?: string;
	noColorLabel?: string;
	useNativeTrigger?: boolean;
}

const numberToHex = (color?: number | null): string => {
	if (color === null || color === undefined) return '';
	return color.toString(16).padStart(8, '0').replace(/^/, '#');
};

const hexToNumber = (hex?: string | null): number | null => {
	if (!hex) return null;
	return parseInt(hex.replace('#', ''), 16) >>> 0;
};

const ColorPickerWithNoColorCheckbox: React.FC<ColorPickerWithNoColorCheckboxProps> = ({ value, onChange, disabled, id, noColorLabel, useNativeTrigger = false }) => {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const swatchRef = useRef<HTMLButtonElement>(null);
	const popupRef = useRef<HTMLDivElement>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [draftHex, setDraftHex] = useState<string>(numberToHex(value));
	const [noColor, setNoColor] = useState<boolean>(value == null);

	useEffect(() => {
		if (!useNativeTrigger) return;
		const swatch = swatchRef.current;
		if (!swatch) return;

		const handleNativeMouseDown = (event: MouseEvent) => {
			event.stopPropagation();
		};
		const handleNativeClick = (event: MouseEvent) => {
			event.preventDefault();
			event.stopPropagation();
			if (disabled) return;
			setIsOpen(current => !current);
		};

		swatch.addEventListener('mousedown', handleNativeMouseDown);
		swatch.addEventListener('click', handleNativeClick);

		return () => {
			swatch.removeEventListener('mousedown', handleNativeMouseDown);
			swatch.removeEventListener('click', handleNativeClick);
		};
	}, [disabled, useNativeTrigger]);

	useEffect(() => {
		setDraftHex(numberToHex(value));
		setNoColor(value == null);
	}, [value]);

	useEffect(() => {
		if (!isOpen) return;

		const handleOutsideClick = (evt: MouseEvent) => {
			const target = evt.target as Node | null;
			const el = target as HTMLElement | null;
			const insideTrigger = target ? wrapperRef.current?.contains(target) : false;
			const insidePopup = target ? popupRef.current?.contains(target) : false;
			const insidePicker = el?.closest?.('.e-colorpicker, .e-colorpicker-wrapper, .e-container');
			if (insideTrigger || insidePopup || insidePicker) return;
			setIsOpen(false);
		};

		document.addEventListener('mousedown', handleOutsideClick);

		return () => {
			document.removeEventListener('mousedown', handleOutsideClick);
		};
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) return;
		const cancelBtn = popupRef.current?.querySelector('.e-cancel') as HTMLElement | null;
		const onCancel = (evt: Event) => {
			evt.preventDefault();
			setDraftHex(numberToHex(value));
			setNoColor(value == null);
			setIsOpen(false);
		};
		cancelBtn?.addEventListener('click', onCancel);
		return () => cancelBtn?.removeEventListener('click', onCancel);
	}, [isOpen, value]);

	const handleSelect = (args: ColorPickerEventArgs) => {
		let hex = args.currentValue.hex;
		if (hex) {
            hex = hex.padEnd(9, 'ff'); // ensure alpha
			setDraftHex(hex);
			if (noColor) setNoColor(false);
		}
	};

	const handleChange = (args: ColorPickerEventArgs) => {
		const hex = args.value || draftHex;
    	const valueToSave = noColor ? null : hexToNumber(hex);
		onChange(valueToSave);
		setIsOpen(false);
	};

	const handleNoColorToggle = (evt: React.ChangeEvent<HTMLInputElement>) => {
		const checked = evt.target.checked;
		setNoColor(checked);
		if (checked) {
			setDraftHex(numberToHex(value));
		}
	};

	const swatchHex = noColor ? 'transparent' : draftHex;
    const valueHex = numberToHex(value);
	const resolvedNoColorLabel = noColorLabel ?? InovxLoader.t('facility:knowledgeBook.noColorDisplayAssetInherent');
	const pickerPopup = (
		<div
			className="kv-color-popup"
			style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
			ref={popupRef}
		>
			<label className="kv-no-color-toggle">
				<input
					type="checkbox"
					checked={noColor}
					onChange={handleNoColorToggle}
				/>
				<span>{resolvedNoColorLabel}</span>
			</label>

			<ColorPickerComponent
				id={id}
				value={valueHex}
				inline={true}
				mode="Picker"
				columns={10}
				presetColors={colorPickerPalette}
				showRecentColors={false}
				showButtons={true}
				enableOpacity={true}
				modeSwitcher={true}
				select={handleSelect}
				change={handleChange}
			/>
		</div>
	);

	return (
		<div
			className={`kv-color-picker-wrapper${disabled ? ' is-disabled' : ''}`}
			ref={wrapperRef}
			onMouseDown={event => {
				event.stopPropagation();
			}}
		>
			<button
				type="button"
				className={`kv-color-swatch${noColor ? ' is-no-color' : ''}${disabled ? ' is-disabled' : ''}`}
				style={{ backgroundColor: swatchHex }}
				onClick={event => {
					if (useNativeTrigger) return;
					event.preventDefault();
					event.stopPropagation();
					if (disabled) return;
					setIsOpen(current => !current);
				}}
				title={noColor ? resolvedNoColorLabel : swatchHex}
				ref={swatchRef}
			/>

				{isOpen && !disabled && ReactDOM.createPortal(pickerPopup, document.body)}
		</div>
	);
};

export default ColorPickerWithNoColorCheckbox;
